<script lang="ts">
    import type { PracticeAny } from "@circleofa/acim-engine"
    import Popup from "@components/popup/Popup.svelte"
    import PopupExit from "@components/popup/PopupExit.svelte"
    import PopupHeading from "@components/popup/PopupHeading.svelte"
    import {
        getMeditationAudioType,
        getMeditationListInstance,
        MeditationAudioType,
    } from "@utils/audiobook/audio-meditations"
    import MeditationMultiSelectItem from "./MeditationMultiSelectItem.svelte"
    import Button from "@components/button/Button.svelte"
    import ButtonIcon from "@components/button/ButtonIcon.svelte"
    import { tooltip } from "@utils/tooltip"

    export let practice: PracticeAny

    // Get all meditations for the current lesson and practice
    $: meditations = getMeditationAudioType(practice).asyncAndThen((practiceType) => {
        const lessonMeds = getMeditationListInstance().map((meditationList) =>
            meditationList.forLesson(practice.lesson.number),
        )
        return lessonMeds.map((items) =>
            items
                .filter(
                    (item) =>
                        item.type === practiceType ||
                        (practiceType === MeditationAudioType.morningEvening &&
                            item.type === MeditationAudioType.main),
                )
                // Looks crazy but it's just sorting morning/evening/undefined in that order, and then non-alt/alt within each
                .sort((a, b) =>
                    a.morning === b.morning
                        ? a.alt === b.alt
                            ? 0
                            : a.alt
                              ? 1
                              : -1
                        : a.morning === true
                          ? -1
                          : a.morning === false
                            ? b.morning === true
                                ? 1
                                : -1
                            : 1,
                ),
        )
    })

    $: audioPopupOpen = false
    $: if (practice) audioPopupOpen = false
</script>

{#await meditations}
    <div class="animate-pulse">
        <Button class="px-2 h-min w-min cursor-progress" color="white" disabled go={() => void 0}>
            <ButtonIcon icon="speaker" />
        </Button>
    </div>
{:then meds}
    {#if meds.isOk() && meds.value.length > 0}
        <Button
            class="px-2 h-min w-min"
            color="white"
            go={() => {
                if (meds.value.length === 1) {
                    meds.value[0].play(practice)
                } else {
                    audioPopupOpen = true
                }
            }}
        >
            <ButtonIcon icon="speaker" />
            {#if meds.value.length > 1}
                <div
                    class="absolute dark:bg-neutral-800 lowercase w-5 h-5 rounded-full top-0 right-[1px] text-center flex items-center justify-center text-xs"
                >
                    ×2
                </div>
            {/if}
        </Button>
        {#if audioPopupOpen}
            <Popup on:clickoutside={() => (audioPopupOpen = false)}>
                <PopupExit on:exit={() => (audioPopupOpen = false)} />
                <PopupHeading class="mb-2">
                    <span class="text-sm opacity-60">{practice.type}</span><br />
                    Audio options
                </PopupHeading>
                <div>
                    <ul class="flex flex-col gap-2">
                        {#each meds.value as meditation, index}
                            <MeditationMultiSelectItem
                                {index}
                                on:click={() => {
                                    meditation.play(practice)
                                    audioPopupOpen = false
                                }}
                                {meditation}
                            />
                        {/each}
                    </ul>
                </div>
            </Popup>
        {/if}
    {:else}
        <Button
            class="px-2 h-min w-min"
            color="white"
            disabled
            on:click={() => {
                if (meds.isErr())
                    tooltip("Unable to retrieve audio.<br /> Please try again later.", true, false)
            }}
        >
            {#if meds.isErr()}
                <div
                    class="absolute bg-neutral-200 dark:bg-neutral-800 dark:border-white border border-black w-5 h-5 rounded-full -top-2 -right-2 text-center flex items-center justify-center text-xs"
                >
                    !
                </div>
            {/if}
            <ButtonIcon icon="speaker" />
        </Button>
    {/if}
{/await}
